import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Grid,
  LinearProgress,
  ListItemText,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  PeopleAlt as PeopleAltIcon,
  QueryStats as QueryStatsIcon,
  Schedule as ScheduleIcon,
  Store as StoreIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';

import AgeCard from '../../Components/GatewayContagem/AgeCard';
import GenderCard from '../../Components/GatewayContagem/GenderCard';
import StatCard from '../../Components/GatewayContagem/StatCard';
import VegaTrafficChart from '../../Components/GatewayContagem/VegaTrafficChart';
import DeviceStatusPanel from '../../Components/GatewayContagem/DeviceStatusPanel';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import DateRangePopover from '../../Components/GatewayContagem/DateRangePopover';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import useAlerts from '../../Context/Hooks/useAlerts';
import { deviceAPI } from '../../API/deviceAPI';
import { countingAPI } from '../../API/countingAPI';
import { eventAPI } from '../../API/eventAPI';
import { fmtDwell, buildQueryString, toUtcStartOfDay, toUtcEndOfDay } from '../../Utils/format';
import { getCanonicalSeverity, expandSeverityParam } from '../../Utils/eventHelpers';

// ─── Period toggle ────────────────────────────────────────────────────────────

const PERIODS = ['today', '7d', '30d', 'month', 'custom'];
const PERIOD_KEY = { today: 'today', '7d': 'sevenDays', '30d': 'thirtyDays', month: 'month', custom: 'custom' };

// eslint-disable-next-line react/prop-types
const PeriodToggle = ({ value, onChange, t }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_, v) => v && onChange(v)}
    size="small"
    sx={{
      '& .MuiToggleButton-root': {
        px: 1.5,
        py: 0.5,
        fontSize: '0.8125rem',
        fontWeight: 500,
        textTransform: 'none',
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
        '&.Mui-selected': {
          bgcolor: 'primary.dark',
          color: '#fff',
          borderColor: 'primary.dark',
          '&:hover': { bgcolor: 'primary.main' },
        },
      },
    }}
  >
    {PERIODS.map((p) => (
      <ToggleButton key={p} value={p}>
        {t(`dashboard:filters.${PERIOD_KEY[p]}`)}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toLocalDateStr = (d) => {
  if (!d) return '';
  if (typeof d === 'string') {
    if (d.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(d)) {
      return d.substring(0, 10);
    }
  }
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** UTC-based date string — matches the UTC display used in VegaTrafficChart table */
const toUtcDateStr = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && d.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(d)) {
    return d.substring(0, 10);
  }
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().substring(0, 10);
};

// Merges aggregate rows from multiple stores by period (for chart display)
const mergeByPeriod = (rows) => {
  const map = new Map();
  for (const r of rows) {
    if (!map.has(r.period)) {
      map.set(r.period, { ...r });
    } else {
      const ex = map.get(r.period);
      const prevIn = ex.totalInCount;
      ex.totalInCount += r.totalInCount;
      ex.totalOutCount += r.totalOutCount;
      ex.totalMaleCount = (ex.totalMaleCount || 0) + (r.totalMaleCount || 0);
      ex.totalFemaleCount = (ex.totalFemaleCount || 0) + (r.totalFemaleCount || 0);
      ex.totalChildCount = (ex.totalChildCount || 0) + (r.totalChildCount || 0);
      ex.totalAdultCount = (ex.totalAdultCount || 0) + (r.totalAdultCount || 0);
      ex.totalElderCount = (ex.totalElderCount || 0) + (r.totalElderCount || 0);
      const w1 = prevIn;
      const w2 = r.totalInCount;
      const total = w1 + w2;
      ex.avgDwellSeconds = total > 0
        ? Math.round(((ex.avgDwellSeconds || 0) * w1 + (r.avgDwellSeconds || 0) * w2) / total)
        : 0;
    }
  }
  return Array.from(map.values());
};

const computePeriodDates = (period) => {
  const now = new Date();
  const display = new Date();
  const comp = new Date();

  if (period === 'today') {
    display.setHours(0, 0, 0, 0);
    // 15 days back so daily summary covers sparkline (7d) + weekTrend comparison (prev 7d)
    comp.setDate(comp.getDate() - 15);
    comp.setHours(0, 0, 0, 0);
  } else if (period === '7d') {
    display.setDate(display.getDate() - 6);
    display.setHours(0, 0, 0, 0);
    comp.setDate(comp.getDate() - 13);
    comp.setHours(0, 0, 0, 0);
  } else if (period === '30d') {
    display.setDate(display.getDate() - 29);
    display.setHours(0, 0, 0, 0);
    comp.setDate(comp.getDate() - 59);
    comp.setHours(0, 0, 0, 0);
  } else if (period === 'month') {
    display.setDate(1);
    display.setHours(0, 0, 0, 0);
    comp.setMonth(comp.getMonth() - 1);
    comp.setDate(1);
    comp.setHours(0, 0, 0, 0);
  } else if (period === 'custom') {
    display.setDate(display.getDate() - 1);
    display.setHours(0, 0, 0, 0);
    comp.setDate(comp.getDate() - 3);
    comp.setHours(0, 0, 0, 0);
    return { startDate: toUtcStartOfDay(display), compStartDate: toUtcStartOfDay(comp), endDate: toUtcEndOfDay(now) };
  }

  return { startDate: toUtcStartOfDay(display), compStartDate: toUtcStartOfDay(comp), endDate: now };
};

// ─── Main page ────────────────────────────────────────────────────────────────

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { selectedClient, stores, loadStores } = useGatewayContagem();
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const { loadAlertsByClient } = useAlerts();
  const [aggregates, setAggregates] = useState([]);
  const [dailySummary, setDailySummary] = useState([]);
  const [dwellSummary, setDwellSummary] = useState([]);
  const [unresolvedEventsCount, setUnresolvedEventsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [period, setPeriod] = useState('today');
  const [granularity, setGranularity] = useState('HOURLY');

  const initialDates = useMemo(() => computePeriodDates('today'), []);
  const [startDate, setStartDate] = useState(initialDates.startDate);
  const [endDate, setEndDate] = useState(initialDates.endDate);
  const [compStartDate, setCompStartDate] = useState(initialDates.compStartDate);

  // Committed state matching current loaded data (prevents chart flash/phantom state during in-flight fetches)
  const [committedPeriod, setCommittedPeriod] = useState('today');
  const [committedStartDate, setCommittedStartDate] = useState(initialDates.startDate);
  const [committedEndDate, setCommittedEndDate] = useState(initialDates.endDate);
  const [committedGranularity, setCommittedGranularity] = useState('HOURLY');

  const [deviceStats, setDeviceStats] = useState({ online: 0, warning: 0, offline: 0, total: 0 });
  const [chartAggregates, setChartAggregates] = useState([]);

  // UTC-based date strings for chart/table filtering (matches UTC display in VegaTrafficChart)
  const utcTodayDateStr = useMemo(() => new Date().toISOString().substring(0, 10), []);
  const utcYesterdayDateStr = useMemo(() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().substring(0, 10);
  }, []);

  // Fetch latest stores list on mount or when client changes
  useEffect(() => {
    if (selectedClient?.id) {
      loadStores(selectedClient.id);
    }
  }, [selectedClient?.id, loadStores]);

  // Reset store filter when client changes
  useEffect(() => { setSelectedStoreIds([]); }, [selectedClient?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Synchronous handler for period changes
  const handlePeriodChange = useCallback((newPeriod) => {
    setIsLoading(true);
    setPeriod(newPeriod);
    setGranularity('HOURLY');
    if (newPeriod) {
      const { startDate: s, compStartDate: c, endDate: e } = computePeriodDates(newPeriod);
      setStartDate(s);
      setCompStartDate(c);
      setEndDate(e);
    }
  }, []);

  // Track active request ID to avoid race conditions and stale response overwrites
  const activeRequestIdRef = useRef(0);

  // Reload counting data when client/store/date range/granularity changes
  const refresh = useCallback(async () => {
    if (!selectedClient?.id) {
      // Keep loading skeleton active while context initializes client
      return;
    }

    const requestId = ++activeRequestIdRef.current;
    setIsLoading(true);

    const activeClientStoreIds = (stores || []).map(s => s.id);
    const validStoreIds = selectedStoreIds.length > 0 ? selectedStoreIds : (activeClientStoreIds.length > 0 ? activeClientStoreIds : null);
    const aggStart = period === 'today'
      ? (() => { const d = new Date(); d.setDate(d.getDate() - 2); d.setHours(0, 0, 0, 0); return d; })()
      : startDate;

    const devicePromise = selectedStoreIds.length > 0
      ? Promise.all(selectedStoreIds.map(id => deviceAPI.getByStore(id))).then(results => {
          const allDevices = results.flatMap(res => res?.value ?? []);
          return {
            online: allDevices.filter(d => d.status === 'ONLINE').length,
            warning: allDevices.filter(d => d.status === 'MAINTENANCE').length,
            offline: allDevices.filter(d => d.status === 'OFFLINE').length,
            total: allDevices.length,
          };
        })
      : deviceAPI.getStatsByClient(buildQueryString({ clientIds: selectedClient.id })).then(res => res?.value ?? { online: 0, warning: 0, offline: 0, total: 0 });

    try {
      const [aggRes, dailyRes, dwellRes, eventsRes, devStatsRes] = await Promise.all([
        countingAPI.getAggregates(selectedClient.id, aggStart, endDate, 'HOUR', null, null, validStoreIds),
        countingAPI.getDailySummary(selectedClient.id, compStartDate, endDate, null, validStoreIds),
        countingAPI.getDwellSummary(selectedClient.id, startDate, endDate, validStoreIds, 1, 1000),
        eventAPI.list({
          clientId: selectedClient.id,
          isResolved: false,
          severityLevel: expandSeverityParam('CRITICAL'),
          itemsPerPage: 100,
        }),
        devicePromise,
      ]);

      if (requestId !== activeRequestIdRef.current) {
        return;
      }

      setAggregates(aggRes?.value || []);
      const dailyItems = dailyRes?.value?.items || dailyRes?.value?.Items || dailyRes?.value || dailyRes?.items || (Array.isArray(dailyRes) ? dailyRes : []);
      setDailySummary(Array.isArray(dailyItems) ? dailyItems : []);
      const dwellItems = dwellRes?.value?.items || dwellRes?.value?.Items || dwellRes?.value || dwellRes?.items || (Array.isArray(dwellRes) ? dwellRes : []);
      setDwellSummary(dwellItems);

      const eventItems = eventsRes?.value?.items || eventsRes?.value?.Items || eventsRes?.value || eventsRes?.items || (Array.isArray(eventsRes) ? eventsRes : []);
      const criticalEvents = eventItems.filter(e => (!e.isResolved && getCanonicalSeverity(e.severity || e.severityLevel) === 'CRITICAL'));
      setUnresolvedEventsCount(criticalEvents.length);
      setDeviceStats(devStatsRes);

      if (granularity !== 'HOUR' && granularity !== 'HOURLY') {
        const chartRes = await countingAPI.getAggregates(selectedClient.id, aggStart, endDate, granularity, null, null, validStoreIds);
        if (requestId !== activeRequestIdRef.current) {
          return;
        }
        setChartAggregates(chartRes?.value || []);
      } else {
        setChartAggregates([]);
      }

      setCommittedPeriod(period);
      setCommittedStartDate(startDate);
      setCommittedEndDate(endDate);
      setCommittedGranularity(granularity);

      loadAlertsByClient(selectedClient.id);
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [selectedClient?.id, selectedStoreIds, stores, period, granularity, startDate, endDate, compStartDate, loadAlertsByClient]);

  useEffect(() => { refresh(); }, [refresh]);

  // Handlers for manual date selection — switch to custom mode (no period active)
  const handleCustomStartDate = useCallback((date) => {
    setIsLoading(true);
    setPeriod(null);
    setGranularity('HOURLY');
    const startOfDay = toUtcStartOfDay(date);
    setStartDate(startOfDay);
    setCompStartDate(startOfDay);
  }, []);

  const handleCustomEndDate = useCallback((date) => {
    setIsLoading(true);
    setPeriod(null);
    setGranularity('HOURLY');
    setEndDate(toUtcEndOfDay(date));
  }, []);

  // Whether the selected range represents a single calendar day
  const isSingleDay = useMemo(() => {
    if (period === 'today') return true;
    if (startDate && endDate) {
      return toLocalDateStr(startDate) === toLocalDateStr(endDate);
    }
    return false;
  }, [period, startDate, endDate]);

  // ── Today / yesterday hourly aggregates (for 'today' chart and KPIs) ────────

  const todayAggs = useMemo(
    () => aggregates.filter(a => toUtcDateStr(a.period) === utcTodayDateStr),
    [aggregates, utcTodayDateStr],
  );
  const yesterdayAggs = useMemo(
    () => aggregates.filter(a => toUtcDateStr(a.period) === utcYesterdayDateStr),
    [aggregates, utcYesterdayDateStr],
  );

  // Chart data: merged by period so multi-store rows are combined into one value per time slot
  const chartSource = useMemo(() => {
    const raw = mergeByPeriod(granularity !== 'HOUR' && granularity !== 'HOURLY' ? chartAggregates : aggregates);
    if (committedPeriod !== 'today' && committedStartDate) {
      const startStr = toLocalDateStr(committedStartDate);
      return raw.filter((a) => toLocalDateStr(a.period) >= startStr);
    }
    return raw;
  }, [granularity, chartAggregates, aggregates, committedPeriod, committedStartDate]);
  const todayChartAggs = useMemo(
    () => chartSource.filter(a => toUtcDateStr(a.period) === utcTodayDateStr),
    [chartSource, utcTodayDateStr],
  );
  const yesterdayChartAggs = useMemo(
    () => chartSource.filter(a => toUtcDateStr(a.period) === utcYesterdayDateStr),
    [chartSource, utcYesterdayDateStr],
  );

  // ── Period-split daily summary (current period vs previous period) ──────────

  const currentPeriodDaily = useMemo(() => {
    const startStr = toLocalDateStr(startDate);
    const endStr = toLocalDateStr(endDate);
    return dailySummary.filter(d => {
      const rowStr = toLocalDateStr(d.date);
      return rowStr >= startStr && rowStr <= endStr;
    });
  }, [dailySummary, startDate, endDate]);

  const prevPeriodDaily = useMemo(() => {
    const startStr = toLocalDateStr(startDate);
    const compStr = toLocalDateStr(compStartDate);
    return dailySummary.filter(d => {
      const rowStr = toLocalDateStr(d.date);
      return rowStr >= compStr && rowStr < startStr;
    });
  }, [dailySummary, startDate, compStartDate]);

  // ── Visitors in period (Card 1) ───────────────────────────────────────────

  const visitorsInPeriod = useMemo(() => {
    if (period === 'today') {
      return todayAggs.reduce((s, a) => s + a.totalInCount, 0);
    }
    if (isSingleDay) {
      const dailySum = currentPeriodDaily.reduce((s, d) => s + d.totalInCount, 0);
      if (dailySum > 0) return dailySum;
      const dayStr = toLocalDateStr(startDate);
      const dayAggs = aggregates.filter(a => toLocalDateStr(a.period) === dayStr);
      return dayAggs.reduce((s, a) => s + a.totalInCount, 0);
    }
    return currentPeriodDaily.reduce((s, d) => s + d.totalInCount, 0);
  }, [period, isSingleDay, todayAggs, currentPeriodDaily, aggregates, startDate]);

  const visitorsInPrevPeriod = useMemo(
    () => {
      if (period === 'today') {
        const currentHour = new Date().getHours();
        const sameWindowYesterday = yesterdayAggs.filter((a) => {
          const hour = new Date(a.period).getHours();
          return hour <= currentHour;
        });
        return sameWindowYesterday.reduce((s, a) => s + a.totalInCount, 0);
      }
      return prevPeriodDaily.reduce((s, d) => s + d.totalInCount, 0);
    },
    [period, yesterdayAggs, prevPeriodDaily],
  );

  const periodTrend = visitorsInPrevPeriod > 0
    ? Math.round(((visitorsInPeriod - visitorsInPrevPeriod) / visitorsInPrevPeriod) * 100)
    : 0;

  const periodTrendLabel = period === 'today'
    ? t('dashboard:kpi.vsYesterday')
    : t('dashboard:kpi.vsPrevPeriod');

  // Daily sparkline: sum per date across all stores from dailySummary (guaranteed exactly 7 days to avoid single-dot chart rendering)
  const sparkline7d = useMemo(() => {
    const byDate = {};
    dailySummary.forEach(d => {
      const k = toLocalDateStr(d.date);
      byDate[k] = (byDate[k] || 0) + d.totalInCount;
    });

    const result = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const localKey = toLocalDateStr(d);
      const value = byDate[localKey] || 0;
      result.push({ v: value });
    }
    return result;
  }, [dailySummary]);

  // Period-aware sparkline for Card 1 (reflects selected period, not always last 7d)
  const periodSparkline = useMemo(() => {
    if (period === 'today') {
      return todayAggs
        .slice()
        .sort((a, b) => new Date(a.period) - new Date(b.period))
        .map(a => ({ v: a.totalInCount }));
    }
    if (isSingleDay) {
      const dayStr = toLocalDateStr(startDate);
      return aggregates
        .filter(a => toLocalDateStr(a.period) === dayStr)
        .slice()
        .sort((a, b) => new Date(a.period) - new Date(b.period))
        .map(a => ({ v: a.totalInCount }));
    }
    const byDate = {};
    currentPeriodDaily.forEach(d => {
      const k = toLocalDateStr(d.date);
      byDate[k] = (byDate[k] || 0) + d.totalInCount;
    });
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => ({ v }));
  }, [period, isSingleDay, todayAggs, currentPeriodDaily, aggregates, startDate]);

  const last7Days = useMemo(
    () => sparkline7d.reduce((s, d) => s + d.v, 0),
    [sparkline7d],
  );

  // Entries from 14–7 days ago: previous 7-day window for weekTrend comparison
  const prev7Total = useMemo(() => {
    const now = Date.now();
    const byDate = {};
    dailySummary.forEach(d => {
      const daysAgo = (now - new Date(d.date).getTime()) / 86400000;
      if (daysAgo >= 7 && daysAgo < 14) {
        const k = toLocalDateStr(d.date);
        byDate[k] = (byDate[k] || 0) + d.totalInCount;
      }
    });
    return Object.values(byDate).reduce((s, v) => s + v, 0);
  }, [dailySummary]);

  const weekTrend = prev7Total > 0
    ? Math.round(((last7Days - prev7Total) / prev7Total) * 100)
    : 0;

  // ── Peak hour (period-aware) ───────────────────────────────────────────────

  const peakAgg = useMemo(() => {
    const src = period === 'today' ? todayAggs : aggregates;
    return src.reduce((max, a) => (!max || a.totalInCount > max.totalInCount ? a : max), null);
  }, [period, todayAggs, aggregates]);

  const peakHour = useMemo(() => {
    if (!peakAgg) return '--';
    const date = new Date(peakAgg.period);
    const h = date.getHours();
    return `${String(h).padStart(2, '0')}:00`;
  }, [peakAgg]);

  const peakVisitors = useMemo(
    () => (peakAgg ? peakAgg.totalInCount.toLocaleString('pt-PT') : '0'),
    [peakAgg],
  );

  // ── Average dwell time (seconds) from DwellSummary endpoint ───────────────

  const avgDwellSeconds = useMemo(() => {
    if (dwellSummary && dwellSummary.length > 0) {
      const withDwell = dwellSummary.filter(r => ((r.visitorCount ?? r.VisitorCount) || 0) > 0 && ((r.avgDwellSeconds ?? r.AvgDwellSeconds) || 0) > 0);
      if (withDwell.length > 0) {
        const totalVis = withDwell.reduce((s, r) => s + ((r.visitorCount ?? r.VisitorCount) || 0), 0);
        if (totalVis > 0) {
          const totalDwellProd = withDwell.reduce((s, r) => s + (((r.avgDwellSeconds ?? r.AvgDwellSeconds) || 0) * ((r.visitorCount ?? r.VisitorCount) || 0)), 0);
          return Math.round(totalDwellProd / totalVis);
        }
      }
      const simpleDwell = dwellSummary.filter(r => ((r.avgDwellSeconds ?? r.AvgDwellSeconds) || 0) > 0);
      if (simpleDwell.length > 0) {
        const sum = simpleDwell.reduce((s, r) => s + ((r.avgDwellSeconds ?? r.AvgDwellSeconds) || 0), 0);
        return Math.round(sum / simpleDwell.length);
      }
    }
    const srcAggs = period === 'today' ? todayAggs : aggregates;
    const withDwell = (srcAggs || []).filter(a => (a.avgDwellSeconds || 0) > 0 && (a.totalInCount || 0) > 0);
    if (!withDwell.length) return 0;
    const totalVis = withDwell.reduce((s, a) => s + (a.totalInCount || 0), 0);
    if (!totalVis) return 0;
    const totalDwellProd = withDwell.reduce((s, a) => s + ((a.avgDwellSeconds || 0) * (a.totalInCount || 0)), 0);
    return Math.round(totalDwellProd / totalVis);
  }, [dwellSummary, period, todayAggs, aggregates]);

  // ── Active stores (period-aware) ──────────────────────────────────────────

  const activeStores = useMemo(() => {
    if (currentPeriodDaily.length > 0) {
      return new Set(currentPeriodDaily.map(d => d.storeId)).size;
    }
    return new Set(aggregates.map(a => a.storeId).filter(Boolean)).size;
  }, [currentPeriodDaily, aggregates]);

  // ── Demographics (period-aware, derived from aggregates) ─────────────────
  const genderSummary = useMemo(() => {
    const startStr = toLocalDateStr(startDate);
    const endStr = toLocalDateStr(endDate);
    const src = period === 'today'
      ? todayAggs
      : aggregates.filter(a => {
          const pStr = toLocalDateStr(a.period);
          return pStr >= startStr && pStr <= endStr;
        });
    return {
      maleCount: src.reduce((s, a) => s + (a.totalMaleCount || 0), 0),
      femaleCount: src.reduce((s, a) => s + (a.totalFemaleCount || 0), 0),
      unclassifiedCount: 0,
    };
  }, [period, todayAggs, aggregates, startDate]);

  const ageSummary = useMemo(() => {
    const src = period === 'today'
      ? todayAggs
      : aggregates.filter(a => toLocalDateStr(a.period) >= toLocalDateStr(startDate));
    const child = src.reduce((s, a) => s + (a.totalChildCount || 0), 0);
    const adult = src.reduce((s, a) => s + (a.totalAdultCount || 0), 0);
    const elder = src.reduce((s, a) => s + (a.totalElderCount || 0), 0);
    return {
      childCount: child,
      adultCount: adult,
      elderCount: elder,
      total: child + adult + elder,
    };
  }, [period, todayAggs, aggregates, startDate]);

  return (
    <Box sx={{ p: 2 }}>
      {isLoading && (
        <LinearProgress sx={{ position: 'absolute', top: 64, left: 0, width: '100%', zIndex: 10 }} />
      )}

      {/* ── Page header ── */}
      <PageHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
            <Typography variant="h2" sx={{ fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.625rem' }, lineHeight: 1.2 }}>
              {t('dashboard:title')}
            </Typography>
            {selectedClient?.name && (
              <Chip
                label={selectedClient.name}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: 'primary.50',
                  height: 26,
                }}
              />
            )}
          </Box>
        }
        storeIndicator={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
            <StoreIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {selectedStoreIds.length === 0
                ? `${t('dashboard:filters.allStores')} (${stores.length})`
                : selectedStoreIds.length === 1
                ? (stores.find(s => s.id === selectedStoreIds[0])?.name || '')
                : `${stores.filter(s => selectedStoreIds.includes(s.id)).map(s => s.name).join(', ')}`}
            </Typography>
          </Box>
        }
      >
        <Select
          size="small"
          multiple
          value={selectedStoreIds}
          onChange={e => {
            const val = e.target.value;
            setIsLoading(true);
            setSelectedStoreIds(typeof val === 'string' ? val.split(',') : val);
          }}
          displayEmpty
          renderValue={selected => (
            selected.length === 0
              ? <span style={{ opacity: 0.55 }}>{t('dashboard:filters.allStores')}</span>
              : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(id => {
                    const store = stores.find(s => s.id === id);
                    return <Chip key={id} label={store?.name || id} size="small" />;
                  })}
                </Box>
          )}
          sx={{ fontSize: '0.8125rem', minWidth: 160, maxWidth: 280 }}
        >
          {stores.map(s => (
            <MenuItem key={s.id} value={s.id} sx={{ fontSize: '0.8125rem' }}>
              <Checkbox checked={selectedStoreIds.includes(s.id)} size="small" />
              <ListItemText primary={s.name} />
            </MenuItem>
          ))}
        </Select>
        <PeriodToggle
          value={period}
          onChange={handlePeriodChange}
          t={t}
        />
        <DateRangePopover
          startDate={startDate}
          endDate={endDate}
          onStartChange={handleCustomStartDate}
          onEndChange={handleCustomEndDate}
          startLabel={t('dashboard:filters.dateStart')}
          endLabel={t('dashboard:filters.dateEnd')}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={() => navigate('/Gateway/Export')}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
        >
          {t('dashboard:filters.export')}
        </Button>
      </PageHeader>

      {/* ── KPI row 1: 4 cards (visitantes, 7d, hora pico, lojas ativas) ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={period === 'today' ? t('dashboard:kpi.visitorsToday') : t('dashboard:kpi.visitorsInPeriod')}
            value={visitorsInPeriod.toLocaleString('pt-PT')}
            trend={periodTrend}
            trendLabel={periodTrendLabel}
            sparklineData={periodSparkline}
            icon={<PeopleAltIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard:kpi.last7Days')}
            value={last7Days.toLocaleString('pt-PT')}
            trend={weekTrend}
            trendLabel={t('dashboard:kpi.vsPreviousWeek')}
            sparklineData={sparkline7d}
            icon={<QueryStatsIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard:kpi.peakHour')}
            value={peakHour}
            subtitle={`${peakVisitors} ${t('dashboard:kpi.visitors')}`}
            icon={<ScheduleIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard:kpi.activeStores')}
            value={activeStores || '--'}
            subtitle={`${deviceStats.total} ${t('dashboard:kpi.devices')}`}
            icon={<StoreIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* ── KPI row 2: 4 cards (permanência, género, faixa etária, estado dispositivos) ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard:kpi.dwell')}
            value={fmtDwell(avgDwellSeconds)}
            icon={<TimerIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GenderCard genderSummary={genderSummary} visitorsInPeriod={visitorsInPeriod} loading={isLoading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AgeCard ageSummary={ageSummary} loading={isLoading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: '14px', boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}>
            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
              <DeviceStatusPanel
                online={deviceStats.online}
                warning={deviceStats.warning}
                offline={deviceStats.offline}
                total={deviceStats.total}
                unresolvedEventsCount={unresolvedEventsCount}
                onViewAll={() => navigate('/Gateway/Devices')}
                onViewEvents={() => navigate('/Gateway/Events')}
                loading={isLoading}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Traffic chart: full width, taller ── */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <VegaTrafficChart
                data={committedPeriod === 'today' ? todayChartAggs : chartSource}
                compareData={committedPeriod === 'today' ? yesterdayChartAggs : []}
                isToday={committedPeriod === 'today'}
                period={committedPeriod}
                startDate={committedStartDate}
                endDate={committedEndDate}
                granularity={committedGranularity}
                onGranularityChange={setGranularity}
                title={t('dashboard:chart.trafficChart')}
                subtitle={committedPeriod === 'today' ? t('dashboard:chart.hourlySubtitle') : t('dashboard:chart.trafficSubtitle')}
                height={430}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
